/**
 * SEMANTIC EXTENSIONS MODEL
 * 
 * Modelo para extensiones dinámicas de la Semantic Layer.
 * Permite evolucionar la ontología sin cambiar código.
 * 
 * TIPOS DE EXTENSIONES:
 * 1. Sinónimos personalizados (admin)
 * 2. Conceptos nuevos (admin)
 * 3. Términos del terapeuta (por terapeuta)
 * 4. Asociaciones aprendidas (automático)
 */

import { ConceptId } from './ontology';
import { AISituationType } from '@/core/types/models/content/user-practice-library.model';

// ============================================
// EXTENSIONES A NIVEL GLOBAL (Admin)
// ============================================

/**
 * Sinónimo personalizado agregado por admin
 */
export interface CustomSynonym {
  id: string;
  
  // El término que queremos mapear
  term: string;           // "agobio", "sobrepasado"
  termNormalized: string; // Lowercase, sin acentos
  
  // A qué concepto mapea
  mapsToConceptId: ConceptId;
  
  // Metadata
  addedBy: string;        // userId del admin
  addedAt: string;
  reason?: string;        // Por qué se agregó
  
  // Estado
  isActive: boolean;
  usageCount: number;     // Cuántas veces se ha usado
}

/**
 * Concepto nuevo agregado por admin
 */
export interface CustomConcept {
  id: string;
  
  // Datos del concepto
  conceptId: string;      // ID único (ej: "trauma_complejo")
  label: string;
  labelEs: string;
  
  // Jerarquía
  parentId: ConceptId;    // Debe existir en ontología base
  level: 2 | 3;           // Solo niveles 2 o 3 (no crear categorías principales)
  
  // Sinónimos
  synonyms: string[];
  
  // Metadata
  addedBy: string;
  addedAt: string;
  reason?: string;
  
  // Estado
  isActive: boolean;
  usageCount: number;
}

// ============================================
// EXTENSIONES A NIVEL TERAPEUTA
// ============================================

/**
 * Término personalizado de un terapeuta
 * Permite que cada terapeuta agregue su propia terminología
 */
export interface TherapistTerm {
  id: string;
  
  // Propiedad del terapeuta
  therapistId: string;
  
  // El término y su mapeo
  term: string;           // "técnica del semáforo", "ejercicio del árbol"
  termNormalized: string;
  mapsToConceptId: ConceptId;
  
  // Opcional: también mapear a una práctica específica
  mapsToPracticeId?: string;
  
  // Contexto de uso
  usedInCategories?: string[];  // Categorías donde aplica
  notes?: string;               // Notas del terapeuta
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  
  // Estado
  isActive: boolean;
  usageCount: number;
}

// ============================================
// APRENDIZAJE AUTOMÁTICO
// ============================================

/**
 * Término buscado que no se encontró
 * Útil para detectar gaps en la ontología
 */
export interface UnmatchedTerm {
  id: string;
  
  // El término que no se encontró
  term: string;
  termNormalized: string;
  
  // Contexto de la búsqueda
  searchContext: 'user_library' | 'therapist_library' | 'ai_assistant' | 'practice_search';
  userId?: string;
  therapistId?: string;
  
  // Frecuencia
  firstSeenAt: string;
  lastSeenAt: string;
  occurrences: number;
  
  // Estado de revisión
  reviewStatus: 'pending' | 'reviewed' | 'mapped' | 'ignored';
  reviewedBy?: string;
  reviewedAt?: string;
  mappedToConceptId?: ConceptId;  // Si se decidió mapearlo
  ignoreReason?: string;           // Por qué se ignoró
}

/**
 * Asociación aprendida del uso real
 * Ej: Usuarios que buscan "no puedo dormir" suelen completar práctica "Respiración 4-7-8"
 */
export interface LearnedAssociation {
  id: string;
  
  // Qué se buscó/dijo
  inputPattern: string;     // "no puedo dormir", "estoy agobiado"
  inputNormalized: string;
  
  // Qué resultó útil
  associatedConceptId?: ConceptId;
  associatedPracticeId?: string;
  
  // Estadísticas
  timesUsed: number;
  timesEffective: number;   // Veces que el usuario dio feedback positivo
  effectivenessScore: number; // timesEffective / timesUsed
  
  // Metadata
  firstSeenAt: string;
  lastSeenAt: string;
  
  // Estado
  isActive: boolean;
  confidenceLevel: 'low' | 'medium' | 'high';  // Basado en estadísticas
}

// ============================================
// DTOs
// ============================================

export interface AddSynonymDTO {
  term: string;
  mapsToConceptId: ConceptId;
  reason?: string;
}

export interface AddConceptDTO {
  conceptId: string;
  label: string;
  labelEs: string;
  parentId: ConceptId;
  synonyms: string[];
  reason?: string;
}

export interface AddTherapistTermDTO {
  therapistId: string;
  term: string;
  mapsToConceptId: ConceptId;
  mapsToPracticeId?: string;
  notes?: string;
}

export interface ReviewUnmatchedTermDTO {
  termId: string;
  action: 'map' | 'ignore';
  mapsToConceptId?: ConceptId;
  ignoreReason?: string;
}

// ============================================
// CONFIGURACIÓN DE LA SEMANTIC LAYER
// ============================================

/**
 * Configuración global de la Semantic Layer
 * Guardada en Firestore para poder modificar sin deploy
 */
export interface SemanticLayerConfig {
  id: 'config';  // Documento único
  
  // Configuración de matching
  matching: {
    minConfidenceThreshold: number;   // 0-1, mínimo para considerar match
    enableFuzzyMatching: boolean;     // Permitir matches aproximados
    fuzzyThreshold: number;           // 0-1, umbral para fuzzy
  };
  
  // Configuración de aprendizaje
  learning: {
    enabled: boolean;
    minOccurrencesToSuggest: number;  // Cuántas veces debe aparecer para sugerir
    autoMapHighConfidence: boolean;   // Auto-mapear si confianza es alta
    autoMapThreshold: number;         // Umbral para auto-map
  };
  
  // Configuración por rol
  therapistExtensions: {
    enabled: boolean;                 // Permitir que terapeutas agreguen términos
    requireApproval: boolean;         // Requiere aprobación de admin
    maxTermsPerTherapist: number;
  };
  
  // Metadata
  updatedAt: string;
  updatedBy: string;
}

// Configuración por defecto
export const DEFAULT_SEMANTIC_CONFIG: SemanticLayerConfig = {
  id: 'config',
  
  matching: {
    minConfidenceThreshold: 0.5,
    enableFuzzyMatching: true,
    fuzzyThreshold: 0.7,
  },
  
  learning: {
    enabled: true,
    minOccurrencesToSuggest: 5,
    autoMapHighConfidence: false,
    autoMapThreshold: 0.9,
  },
  
  therapistExtensions: {
    enabled: true,
    requireApproval: false,
    maxTermsPerTherapist: 50,
  },
  
  updatedAt: new Date().toISOString(),
  updatedBy: 'system',
};





